import debounce from 'lodash-es/debounce';
import { isGulpOrBranching } from 'owa-config/lib/bootstrapOptions';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { trace } from 'owa-trace';

export const SAFE_AREA_MARKER = 'iOSSafeArea';

const WARNING_MESSAGE =
    'Caution: useApplySafeAreaPadding may not work as expected on a scrollable container with fixed/absolute elements. Either avoid fixed/absolute elements or manually add "iOSSafeArea" classname to the element where you think safe area padding should be applied';

/**
 * Applies safe area padding to a given container.
 * @param safeAreaContainerRef The container on which safe area padding needs to be applied
 * @param noSafeArea Boolean to indicate whether to apply safe area padding or not
 *
 * The component, whose ref is passed ( or on which class is applied), should change its dimensions when iOS browser goes in full screen mode.
 * This causes this hook to be called and add the correct padding to the component.
 */
export const useApplySafeAreaPadding = (
    safeAreaContainerRef: RefObject<HTMLElement>,
    noSafeArea: boolean = false
): void => {
    const currentPaddedElements = useRef<HTMLElement[]>([]);
    useEffect(() => {
        let mutationObserver = null;
        // Apply safe area only if it's not disabled and if the app is either running on iOS or in dev environment
        if (!noSafeArea && (isIOS() || isGulpOrBranching())) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
             * See aka.ms/mini-lint-errors
             *	> Unexpected any. Specify a different type. */
            const mutationObserverCb = (mutations: any, mutationObserver: MutationObserver) => {
                requestAnimationFrame(() => {
                    if (!safeAreaContainerRef.current?.scrollHeight) {
                        return;
                    }

                    // Disconnet the observer to avoid infinite loop due to dom changes inside this callback
                    mutationObserver.disconnect();

                    // Get the new elements to apply padding
                    const elementsToApplyPadding = getElementsToApplySafeAreaPadding(
                        safeAreaContainerRef.current
                    );

                    // Reset padding applied on old elemnts and apply padding to new elements
                    currentPaddedElements.current.map(
                        element => (element.style.paddingBottom = '')
                    );
                    elementsToApplyPadding.map(applySafeAreaPadding);
                    currentPaddedElements.current = elementsToApplyPadding;

                    // Start observing again
                    mutationObserver.observe(safeAreaContainerRef.current as HTMLElement, {
                        childList: true,
                        subtree: true,
                    });
                });
            };
            const debouncedMutationObserverCb = debounce(mutationObserverCb, 200, {
                leading: true,
                trailing: true,
            });
            mutationObserver = new MutationObserver(debouncedMutationObserverCb);
            debouncedMutationObserverCb(null, mutationObserver);
        }
        return () => {
            mutationObserver && mutationObserver.disconnect();
        };
    });
};

/**
 * Returns true if an element can scroll
 * @param elem
 */
const canScroll = (elem: HTMLElement): boolean => {
    const elemStyles = getComputedStyle(elem);
    return (
        ['auto', 'scroll'].includes(elemStyles.overflowY) && elem.scrollHeight > elem.clientHeight
    );
};

/**
 * Returns the list of elements for which safe area padding needs to be applied
 * @param container The input container element
 */
const getElementsToApplySafeAreaPadding = (container: HTMLElement): HTMLElement[] => {
    // Get elements which are explictly marked for safe area padding
    const elementsToApplyPadding = Array.from(
        container.getElementsByClassName(SAFE_AREA_MARKER)
    ) as HTMLElement[];

    // Default fallback logic when there are no explicit marked elements
    if (!elementsToApplyPadding.length) {
        // Log warning if a fixed/absolute positioned element is present inside a scrollable element
        if (isGulpOrBranching()) {
            const allElements = Array.from(container.getElementsByTagName('*'));
            allElements.some(
                elem =>
                    ['fixed', 'absolute'].includes(getComputedStyle(elem).position) &&
                    canScroll(elem.parentElement)
            ) && trace.warn(WARNING_MESSAGE);
        }

        // Find the first scrollable element from top
        let currentElement = container;
        while (!!currentElement && !canScroll(currentElement)) {
            currentElement = currentElement.lastElementChild as HTMLElement;
        }

        elementsToApplyPadding.push(currentElement || container);
    }
    return elementsToApplyPadding;
};

/**
 * Applies safe area padding to the input element
 * @param elem Element to which safe area padding is needed
 */
const applySafeAreaPadding = (elem: HTMLElement) => {
    const safeAreaBottomPadding = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sab')
    );
    if (safeAreaBottomPadding) {
        const currentPaddingBottom = parseFloat(getComputedStyle(elem).paddingBottom);
        elem.style.paddingBottom = `${currentPaddingBottom + safeAreaBottomPadding}px`;
    }
};
